import { useEffect, useState } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader } from './Stepper.jsx';
import ConfirmCancelOrderModal from './ConfirmCancelOrderModal.jsx';
import { brl } from '../utils/format.js';
import { listOrders } from '../api/orders.js';

const ORDER_STATUS_META = {
  agendado: { label: 'Pago', color: 'var(--marble)', bg: 'var(--marble-soft)' },
  coletado: { label: 'Coletado', color: 'var(--warning-dark)', bg: 'var(--overlay-warning-16)' },
  resultado: { label: 'Resultado disponível', color: 'var(--success)', bg: 'var(--green-soft)' },
  cancelado: { label: 'Cancelado', color: 'var(--danger)', bg: 'var(--overlay-danger-08)' },
};

const FILTERS = [
  { k: null, l: 'Todos' },
  { k: 'agendado', l: 'Pago' },
  { k: 'coletado', l: 'Coletado' },
  { k: 'resultado', l: 'Resultado' },
  { k: 'cancelado', l: 'Cancelado' },
];

// Meus Pedidos — histórico, cada card pode linkar para o resultado.
// Filtro por status é resolvido no servidor (GET /orders?status=), não
// client-side como no protótipo de design.
const CANCEL_WINDOW_DAYS = 7;

// Só um espelho no front da regra real (aplicada e validada no backend) —
// evita mostrar o botão de cancelar num pedido que a API certamente recusaria.
function withinCancelWindow(order) {
  const days = (Date.now() - new Date(order.created_at).getTime()) / 86_400_000;
  return days <= CANCEL_WINDOW_DAYS;
}

export default function OrdersScreen({ onBack, onOpenResult }) {
  const [filter, setFilter] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    listOrders({ status: filter })
      .then((data) => { if (alive) setOrders(data); })
      .catch(() => { if (alive) setError('Não foi possível carregar seus pedidos.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <MobHeader title="Meus Pedidos" onBack={onBack}/>
      <div style={{ flex: 1, padding: '16px 16px 24px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
          {FILTERS.map((t) => (
            <button key={t.l} onClick={() => setFilter(t.k)} style={{
              padding: '7px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
              background: filter === t.k ? 'var(--midnight)' : 'var(--white)',
              color: filter === t.k ? 'var(--white)' : 'var(--text-muted)',
              border: '1px solid ' + (filter === t.k ? 'var(--midnight)' : 'var(--border)'),
              fontFamily: 'var(--font-display)',
            }}>{t.l}</button>
          ))}
        </div>

        {error && (
          <div role="alert" style={{ marginTop: 14, padding: '10px 14px', background: 'var(--danger-tint)', borderRadius: 10, color: 'var(--danger-strong)', fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-subtle)', fontSize: 13.5 }}>
            Carregando…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
            {orders.map((order) => {
              const meta = ORDER_STATUS_META[order.ui_status] || ORDER_STATUS_META.agendado;
              const dt = new Date(order.created_at);
              const dateStr = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <div key={order.id} className="card" style={{ padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5, color: 'var(--midnight)' }}>
                        {order.protocol_number || order.id}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>{dateStr}</div>
                      {order.security_token && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 3, display: 'flex', alignItems: 'baseline', gap: 5 }}>
                          Token
                          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5, color: 'var(--midnight)', letterSpacing: '0.04em' }}>
                            {order.security_token}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="chip" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                  </div>

                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {order.items.map((i) => (
                      <div key={i.id} style={{ display: 'flex', gap: 6, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                        <span style={{ flexShrink: 0 }}>-</span>
                        <span>{i.tuss_name}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--midnight)' }}>{order.partner_name || 'A confirmar'}</div>
                      {order.partner_address && (
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 2, lineHeight: 1.35 }}>{order.partner_address}</div>
                      )}
                      {order.partner_phone && (
                        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 1 }}>{order.partner_phone}</div>
                      )}
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--midnight)', flexShrink: 0, marginLeft: 10 }}>
                      R$ {brl(order.total_amount)}
                    </div>
                  </div>

                  {order.ui_status === 'resultado' && (
                    <button onClick={() => onOpenResult(order)} style={{
                      marginTop: 10, width: '100%', padding: '9px 0', borderRadius: 8,
                      background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
                      fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}>
                      Ver resultado <Icon.arrowR size={14} color="var(--brand-primary)"/>
                    </button>
                  )}

                  {order.ui_status === 'agendado' && withinCancelWindow(order) && (
                    <button onClick={() => setCancelTarget(order)} style={{
                      marginTop: 8, width: '100%', padding: '9px 0', borderRadius: 8,
                      background: 'transparent', color: 'var(--danger)',
                      fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 12.5,
                      border: '1px solid var(--border)',
                    }}>
                      Cancelar pedido
                    </button>
                  )}
                </div>
              );
            })}
            {orders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-subtle)', fontSize: 13.5 }}>
                Nenhum pedido nesse status.
              </div>
            )}
          </div>
        )}
      </div>

      {cancelTarget && (
        <ConfirmCancelOrderModal
          order={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={(updated) => {
            setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
          }}
        />
      )}
    </div>
  );
}
